/**
 * @author: lv.chen
 * @desc: Ranks榜单
 * @date: 2025/12/15
 */
import React from 'react';
import classnames from 'classnames';
import { Popover } from 'tea-component';
import GameImage from '@home/components/GameImage';
import EllipsisHover from '@home/components/EllipsisHover';
import NoData from '@intelligence/components/NoData';
import RanksTop3MiniGame from './RanksTop3MiniGame';
import AppstoreRankDetailsModal from '../AppstoreRankDetailsModal';
import { isEmpty } from '@intelligence/v3/utils/common';
import { formatPercentNum } from '@intelligence/v3/utils/number';
import { getCountryFlag } from '@home/_config/countries';
import { valueFormat } from '@intelligence/utils/commonUtils';
import { formatCasualDailyReportDate, getRankElement, getDodClassName } from '../../utils';

import IMG_TITLE_RANK from '@intelligence/assets/imgs/casualDailyReports/title-rank.png';
import IMG_APP_STORE from '@intelligence/assets/imgs/casualDailyReports/icon-appstore.png';
import IMG_ICON_HUOYUE from '@intelligence/assets/imgs/casualDailyReports/icon-huoyue.png';
import IMG_WEIXIN from '@intelligence/assets/imgs/casualDailyReports/icon-weixin.png';
import IMG_DOUYIN from '@intelligence/assets/imgs/casualDailyReports/icon-dy.png';

interface IRanksProps extends IBaseProps {
  date: string;
  sensortowerDauRanksDate: string;
  appstoreFreeRanks?: IAppStoreFreeRankItem[] | null;
  sensortowerDauRanks?: ISensorTowerDauItem[] | null;
  onGoToGameDetails?: (game?: any, tab?: string) => void;
  /** 小游戏排名模块数据 */
  miniGameRankModules?: IMiniGameRankModules | null;
}

/**
 * 检查 SensorTower DAU 榜单是否所有游戏都没有有效的 DAU 数据
 */
const isAllDauDataEmpty = (ranks: ISensorTowerDauItem[] | null | undefined): boolean => {
  if (isEmpty(ranks)) {
    return false;
  }

  return ranks!.every((item) => {
    const itemDau = item?.dau;
    const itemDauDod = item?.dau_dod;
    return isEmpty(itemDau) && isEmpty(itemDauDod);
  });
};

const Ranks: React.FC<IRanksProps> = (props) => {
  const {
    style,
    className,
    date,
    sensortowerDauRanksDate,
    appstoreFreeRanks,
    sensortowerDauRanks,
    onGoToGameDetails,
    miniGameRankModules,
  } = props;

  const showAppstoreFreeRanks = !!appstoreFreeRanks?.length;
  const showSensortowerDauRanks = !!sensortowerDauRanks?.length;

  // 检查是否有小游戏榜单数据
  const hasWeixinTop3 =
    !!miniGameRankModules?.wechat_popularity_top?.length || !!miniGameRankModules?.wechat_most_played_top?.length;
  const hasDouyinTop3 =
    !!miniGameRankModules?.douyin_popularity_top?.length || !!miniGameRankModules?.douyin_fresh_game_top?.length;

  const renderSensortowerDauRankCard = () => {
    // 判断是否需要显示空状态（有游戏列表但无 DAU 数据）
    if (showSensortowerDauRanks && isAllDauDataEmpty(sensortowerDauRanks)) {
      return <NoData text="官方数据调整中，暂无数据" />;
    }

    return sensortowerDauRanks!.map((sensortowerDauRank, index) => {
      const sensortowerDauRankDod = sensortowerDauRank?.dau_dod ?? 0;
      const sensortowerDauRankDodFormatted = formatPercentNum(sensortowerDauRankDod, {
        decimalPlaces: 1,
      });

      return (
        <div
          key={index}
          className="dbi-rank-card dbi-cursor-pointer"
          onClick={(e) => {
            e.stopPropagation();
            onGoToGameDetails?.(sensortowerDauRank);
          }}
        >
          <div className="dbi-rank-card-index">{getRankElement(index)}</div>
          <div className="dbi-rank-card-info">
            <div className="dbi-rank-card-info-avatar">
              <GameImage entityType="mobile" src={sensortowerDauRank?.cover} />
            </div>
            <div className="dbi-rank-card-info-text">
              <div className="dbi-rank-card-info-title">
                <EllipsisHover text={sensortowerDauRank?.entity_name || ''} />
              </div>
              <div className="dbi-rank-card-info-desc">
                <img
                  className="dbi-rank-card-info-desc-img"
                  src={getCountryFlag(sensortowerDauRank?.publisher_country_abbre)}
                  alt="rank"
                />
                <EllipsisHover text={sensortowerDauRank?.publisher || ''} />
              </div>
            </div>
          </div>
          <div className="dbi-rank-card-info-trend">
            <div className="dbi-rank-info-trend-percent">
              <span className="s1">{valueFormat(sensortowerDauRank?.dau)}</span>
              {sensortowerDauRankDodFormatted && (
                <span className={classnames('s2', getDodClassName(sensortowerDauRankDod))}>
                  {sensortowerDauRankDodFormatted}
                </span>
              )}
            </div>
          </div>
        </div>
      );
    });
  };

  return (
    <div style={style} className={classnames('dbi-casual-daily-reports-ranks dbi-section-rank', className)}>
      <div className="dbi-section-rank-title">
        <img className="dbi-section-production-title-img" src={IMG_TITLE_RANK} alt="rank" />
      </div>

      <div className="dbi-section-rank-content-wrap">
        {showAppstoreFreeRanks && (
          <div className="dbi-section-rank-content">
            <div className="dbi-section-rank-date">榜单数据日期：{formatCasualDailyReportDate(date)}</div>
            <div className="dbi-section-rank-box">
              <div className="dbi-section-rank-box-wrap">
                <div className="dbi-section-rank-box-hd">
                  <div className="dbi-section-rank-box-hd-title">
                    <img className="dbi-section-production-title-img" src={IMG_APP_STORE} alt="rank" />
                    <span>AppStore 免费榜 - 休闲游戏</span>
                  </div>
                  <div className="dbi-section-rank-box-hd-desc">
                    <p className="dbi-rank-card-country">国家数</p>
                    <p className="dbi-rank-card-num10">上榜前十的国家数量</p>
                  </div>
                </div>
                <div className="dbi-section-rank-box-bd">
                  {appstoreFreeRanks.map((appstoreFreeRank, index) => {
                    const appstoreFreeRankMarketCountDod = appstoreFreeRank?.market_count_dod;

                    return (
                      <div
                        key={index}
                        className="dbi-rank-card dbi-cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          onGoToGameDetails?.(appstoreFreeRank, 'storeRank');
                        }}
                      >
                        <div className="dbi-rank-card-index">{getRankElement(index)}</div>
                        <div className="dbi-rank-card-info">
                          <div className="dbi-rank-card-info-avatar">
                            <GameImage entityType="mobile" src={appstoreFreeRank?.cover} />
                          </div>
                          <div className="dbi-rank-card-info-text">
                            <div className="dbi-rank-card-info-title">
                              <EllipsisHover text={appstoreFreeRank?.entity_name || ''} />
                            </div>
                            <div className="dbi-rank-card-info-desc">
                              <img
                                className="dbi-rank-card-info-desc-img"
                                src={getCountryFlag(appstoreFreeRank?.publisher_country_abbre)}
                                alt="rank"
                              />
                              <EllipsisHover text={appstoreFreeRank?.publisher || ''} />
                            </div>
                          </div>
                        </div>
                        <div className="dbi-rank-card-info-trend">
                          <Popover
                            placement="top"
                            overlay={<AppstoreRankDetailsModal data={appstoreFreeRank?.cur_market_rank_json} />}
                          >
                            <div className="dbi-rank-info-trend-percent">
                              <span className="s1">{appstoreFreeRank?.cur_market_count}</span>
                              {typeof appstoreFreeRankMarketCountDod === 'number' && (
                                <span className={classnames('s2', getDodClassName(appstoreFreeRankMarketCountDod))}>
                                  {appstoreFreeRankMarketCountDod > 0 && <span>+</span>}
                                  {appstoreFreeRankMarketCountDod}
                                </span>
                              )}
                            </div>
                          </Popover>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        )}

        {showSensortowerDauRanks && (
          <div className="dbi-section-rank-content dbi-section-rank-content02 dbi-sensortower-dau-rank-section">
            <div className="dbi-section-rank-date">
              榜单数据日期：
              {formatCasualDailyReportDate(sensortowerDauRanksDate)}
            </div>
            <div className="dbi-section-rank-box">
              <div className="dbi-section-rank-box-wrap">
                <div className="dbi-section-rank-box-hd">
                  <div className="dbi-section-rank-box-hd-title">
                    <img className="dbi-section-production-title-img" src={IMG_ICON_HUOYUE} alt="rank" />
                    <span>Sensor Tower 日活跃榜单</span>
                  </div>
                  <div className="dbi-section-rank-box-hd-desc">
                    <p className="dbi-rank-card-country">DAU</p>
                  </div>
                </div>
                <div className="dbi-section-rank-box-bd">{renderSensortowerDauRankCard()}</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* 三期增加微信和抖音排行 */}
      {(hasWeixinTop3 || hasDouyinTop3) && (
        <div className="dbi-section-rank-content-wrap dbi-section-rank-content-wrap--app">
          {hasWeixinTop3 && (
            <RanksTop3MiniGame
              title="微信小游戏榜单Top3-国区"
              iconAlt="微信图标"
              platformIcon={IMG_WEIXIN}
              firstRankTitle="人气榜"
              firstRankClass="--popularity"
              secondRankTitle="畅玩榜"
              secondRankClass="--enjoy"
              date={date}
              popularityTopRanks={miniGameRankModules?.wechat_popularity_top}
              secondTopRanks={miniGameRankModules?.wechat_most_played_top}
            />
          )}
          {hasDouyinTop3 && (
            <RanksTop3MiniGame
              title="抖音小游戏榜单Top3-国区"
              iconAlt="抖音图标"
              platformIcon={IMG_DOUYIN}
              date={date}
              popularityTopRanks={miniGameRankModules?.douyin_popularity_top}
              secondTopRanks={miniGameRankModules?.douyin_fresh_game_top}
              firstRankTitle="热门榜"
              firstRankClass="--remen"
              secondRankTitle="新游榜"
              secondRankClass="--xinyou"
            />
          )}
        </div>
      )}
    </div>
  );
};

export default Ranks;
